import React, { useEffect, useState } from 'react';
import {
    DialogActions,
    DialogContentText,
    DialogContent,
    DialogTitle,
    Dialog,
    Alert,
    Box,
    Grid2,
    TextField,
    CircularProgress,
    Typography,
    Divider,
    Paper,
    Stack,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import CommonButton from '../components/atom/CommonButton';
import { useTranslation } from 'react-i18next';
import { useGetBookingItems } from '@openapi/booking-items/booking-items';
import { useGetOrders } from '@openapi/orders/orders';
import { formatCurrency } from '../pages/bookings/helpers/formatters';
import { TipTapRenderer } from '../components/atom/TipTapRenderer';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const CancellationConfirmationModal = ({
    title,
    content,
    bookingId,
    startTime,
    cancelWindow,
    cancelDescription,
    confirm,
    warningContent = false,
    showModal,
    MODAL_TYPE,
    alwaysRefund = false,
}) => {
    const { t } = useTranslation();
    const closeAction = () => showModal(MODAL_TYPE.NONE);

    // agent aided cancellation (input only)
    const [inputValue, setInputValue] = useState(0);
    const minValue = 0;
    // Initial 0 is the right default for the stale-processing case (no items
    // → nothing to refund). For bookings with items, the useEffect below
    // overwrites with the computed order total once the data loads.
    const [maxValue, setMaxValue] = useState(0);
    const [orderTotal, setOrderTotal] = useState(0);
    const [processingFeesTotal, setProcessingFeesTotal] = useState(0);
    const [marketing, setMarketing] = useState({});
    // When the agent has the BOOKINGS_ALWAYS_REFUND capability and the booking
    // is outside its cancel window, default to the venue's policy (no refund).
    // The agent must explicitly opt in to override the policy — that toggle is
    // the source of truth for both the UI and the API flag we send.
    const [overrideCancelPolicy, setOverrideCancelPolicy] = useState(false);
    const handleInputChange = (event) => {
        const value = event.target.value === '' ? 0 : Number(event.target.value * 100);
        setInputValue(value);
    };
    const handleBlur = () => {
        if (inputValue < minValue) {
            setInputValue(minValue);
        } else if (inputValue > maxValue) {
            setInputValue(maxValue);
        }
    };

    const {
        data,
        isLoading: loading,
        error,
    } = useGetBookingItems({
        optionalId: bookingId,
        pageSize: 10000,
    });

    const {
        data: orderData,
        isLoading: orderLoading,
        error: orderError,
    } = useGetOrders({
        optionalId: bookingId,
        pageSize: 10000,
        includeTransactions: true, // Include transactions to get processing fees
    });

    // set maxValue after data is loaded for the first time
    useEffect(() => {
        if (
            data &&
            Array.isArray(data.bookingItems) &&
            data.bookingItems.length > 0 &&
            orderData &&
            Array.isArray(orderData.orders)
        ) {
            // set marketing info from the first booking item
            setMarketing(data.bookingItems[0]?.show.marketing || {});

            // each item has a 'price' property, sum them up
            const calcOrderTotal = orderData.orders
                .filter((e) => e.status !== 'rollback')
                .reduce((sum, item) => sum + (item.amount || 0), 0);

            // Calculate total processing fees from all transactions across all orders
            const calcProcessingFees = orderData.orders
                .filter((e) => e.status !== 'rollback')
                .reduce((sum, order) => {
                    const orderProcessingFees = order.transactions?.reduce(
                        (txSum, tx) => txSum + (tx.processingFee || 0),
                        0,
                    );
                    return sum + (orderProcessingFees || 0);
                }, 0);

            // Total refund includes order amounts + processing fees
            const total = calcOrderTotal + calcProcessingFees;

            setOrderTotal(calcOrderTotal);
            setProcessingFeesTotal(calcProcessingFees);
            setMaxValue(total);
            setInputValue(total);
        }
    }, [data, orderData]);

    let confirmButtonText = t('common.yes'); // default confirmation button text
    let extraContent = null;

    const refundBreakdown = (
        <Paper elevation={0} sx={{ bgcolor: 'grey.50', p: 2, mb: 2 }}>
            <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                        {t('orders.columns.amount')}:
                    </Typography>
                    <Typography variant="body2">{formatCurrency(orderTotal / 100)}</Typography>
                </Box>
                {processingFeesTotal > 0 && (
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                            {t('transactions.columns.processing_fee')}:
                        </Typography>
                        <Typography variant="body2">
                            {formatCurrency(processingFeesTotal / 100)}
                        </Typography>
                    </Box>
                )}
                <Divider sx={{ my: 0.5 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight="bold">
                        {t('transactions.columns.total')}:
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        {formatCurrency(maxValue / 100)}
                    </Typography>
                </Box>
            </Stack>
        </Paper>
    );

    const showRefundInput = (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" id="input-refund" sx={{ mb: 1 }}>
                {t('refundChunks.refundAmount')}:
            </Typography>
            <TextField
                value={inputValue / 100}
                type="number"
                size="medium"
                fullWidth
                onChange={handleInputChange}
                onBlur={handleBlur}
                inputProps={{
                    step: 1,
                    min: minValue / 100,
                    max: maxValue / 100,
                    type: 'number',
                    'aria-labelledby': 'input-refund',
                }}
                InputProps={{
                    startAdornment: (
                        <Typography component="span" sx={{ mr: 1, color: 'text.secondary' }}>
                            {formatCurrency(0).replace(/[0-9.,\s]+/, '')}
                        </Typography>
                    ),
                }}
            />
        </Box>
    );

    if (loading || orderLoading) {
        extraContent = (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress />
            </Box>
        );
    } else if (error || orderError) {
        extraContent = (
            <Alert severity="error" sx={{ my: 2 }}>
                {t('common.errorLoadingData')}
            </Alert>
        );
    } else {
        const hasCancelWindow = !!cancelWindow && cancelWindow > 0;
        let insideCancelWindow = true;
        if (hasCancelWindow) {
            const cancelTime = new Date(startTime);
            cancelTime.setHours(cancelTime.getHours() - cancelWindow);
            insideCancelWindow = new Date() < cancelTime;
        }
        // No cancel window configured ⇒ no restriction ⇒ refund flows normally.
        // Outside an active window, the always-refund capability lets the agent
        // override the policy — but only when they explicitly opt in.
        const canOverridePolicy = !insideCancelWindow && alwaysRefund;
        const refundAllowed = insideCancelWindow || (canOverridePolicy && overrideCancelPolicy);

        const overrideToggle = canOverridePolicy && (
            <Paper
                elevation={0}
                variant="outlined"
                sx={{ p: 2, mb: 2, borderColor: 'warning.main', bgcolor: 'warning.lighter' }}
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={overrideCancelPolicy}
                            onChange={(e) => {
                                const next = e.target.checked;
                                setOverrideCancelPolicy(next);
                                // Default to 0 in both directions: turning the
                                // override on must NOT silently grant a full
                                // refund — the agent has to type an amount.
                                setInputValue(0);
                            }}
                        />
                    }
                    label={
                        <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                                {t('bookings.overrideCancelPolicyLabel')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('bookings.overrideCancelPolicyHelp')}
                            </Typography>
                        </Box>
                    }
                />
            </Paper>
        );

        if (refundAllowed) {
            extraContent = (
                <Box>
                    {overrideToggle}
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {t('bookings.cancelBookingWithRefund', {
                            refundValue: formatCurrency(inputValue / 100),
                        })}
                    </Alert>
                    {refundBreakdown}
                    {showRefundInput}
                </Box>
            );
            confirmButtonText =
                inputValue === maxValue
                    ? t('bookings.cancelBookingFullRefund')
                    : t('bookings.cancelBookingWithRefund', {
                          refundValue: formatCurrency(inputValue / 100),
                      });
        } else {
            extraContent = (
                <Box>
                    {overrideToggle}
                    <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
                        {t('bookings.cancelBookingWithNoRefund')}
                    </Alert>
                    {cancelDescription && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                {t('shows.columns.cancelDescription')}:
                            </Typography>
                            <TipTapRenderer content={cancelDescription} />
                        </Box>
                    )}
                    {refundBreakdown}
                    {showRefundInput}
                    <Paper elevation={0} sx={{ bgcolor: 'info.light', p: 2, mt: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                            {t('shows.contactVenue')}:
                        </Typography>
                        <Typography variant="body2">
                            {marketing.email} • {marketing.phone}
                        </Typography>
                    </Paper>
                </Box>
            );
            confirmButtonText = t('bookings.cancelBookingWithNoRefund');
        }
    }

    return (
        <Dialog
            open
            onClose={closeAction}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-content">{content}</DialogContentText>
                {warningContent && (
                    <DialogContentText id="alert-dialog-warning">
                        <Alert sx={{ mt: 0.5 }} severity="warning">
                            {warningContent}
                        </Alert>
                    </DialogContentText>
                )}
                {extraContent}
            </DialogContent>
            <DialogActions>
                <CommonButton
                    onClick={closeAction}
                    autoFocus
                    label={t('common.close')}
                    buttonType="secondary"
                />
                <CommonButton
                    onClick={() => {
                        confirm(bookingId, inputValue, { overrideCancelPolicy });
                        showModal(MODAL_TYPE.NONE);
                    }}
                    label={confirmButtonText}
                    buttonType="primary"
                />
            </DialogActions>
        </Dialog>
    );
};

export default CancellationConfirmationModal;
